import React, { useMemo } from 'react';
import { Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const options = [
  {
    value: 'social',
    title: 'Social media',
    subtitle: 'Instagram • Reddit • X',
  },
  {
    value: 'friends',
    title: 'Friends',
    subtitle: 'Word of mouth',
  },
  {
    value: 'youtube',
    title: 'YouTube',
    subtitle: 'Videos or reviews',
  },
  {
    value: 'ads',
    title: 'Ads',
    subtitle: 'Search or display',
  },
];

const WaitlistSourceDialog = ({
  open,
  onOpenChange,
  onSelect,
  onSubmit,
  status = 'idle', // idle | loading | success | already
  selected,
  resultMessage,
}) => {
  const isBusy = status === 'loading';
  const isSuccess = status === 'success';
  const isAlready = status === 'already';

  const title = useMemo(() => {
    if (isAlready) return 'Already on the list';
    if (isSuccess) return 'Thanks!';
    return 'How did you hear about us?';
  }, [isAlready, isSuccess]);

  const description = useMemo(() => {
    if (isAlready) {
      return (
        resultMessage ||
        "We get it — you can't wait. We'll get back to you real quick."
      );
    }
    if (isSuccess) return resultMessage || "You're on the waitlist. We'll inform you soon.";
    return 'This helps us focus on what’s working.';
  }, [isAlready, isSuccess, resultMessage]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[520px] rounded-2xl border border-black/10 bg-white p-6 shadow-[0_30px_90px_rgba(0,0,0,0.18)]"
        style={{ fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }}
      >
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-left text-xl font-semibold text-[#111111]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-left text-sm text-black/60">
            {description}
          </DialogDescription>
        </DialogHeader>

        {isSuccess || isAlready ? (
          <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#9be38f]/35 bg-[#9be38f]/10 px-4 py-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#9be38f] text-[#111111]">
              <Check className="h-5 w-5" />
            </span>
            <div className="text-sm font-medium text-[#111111]">
              {isAlready ? 'You’re already on the waitlist.' : 'Submitted.'}
            </div>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-3">
            {options.map((opt) => {
              const active = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={isBusy}
                  onClick={() => onSelect(opt.value)}
                  className={
                    'w-full rounded-xl border px-4 py-3 text-left transition-all duration-200 ' +
                    (active
                      ? 'border-[#9be38f] bg-[#9be38f]/10 shadow-[0_10px_26px_rgba(155,227,143,0.18)]'
                      : 'border-black/10 bg-white hover:bg-black/[0.02]') +
                    (isBusy ? ' opacity-70' : '')
                  }
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="text-[15px] font-semibold text-[#111111]">{opt.title}</div>
                      <div className="mt-0.5 text-[13px] text-black/55">{opt.subtitle}</div>
                    </div>
                    {active ? (
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#9be38f] text-[#111111]">
                        <Check className="h-4 w-4" />
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}

            <div className="mt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={isBusy}
                onClick={() => onOpenChange(false)}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-black/70 hover:bg-black/[0.04] transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isBusy || !selected}
                onClick={() => onSubmit(selected)}
                className="rounded-lg px-5 py-2 text-sm font-semibold text-[#111111] bg-[#9be38f] hover:bg-[#8ad37f] transition-colors disabled:opacity-60 disabled:hover:bg-[#9be38f]"
              >
                {isBusy ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {isSuccess || isAlready ? (
          <div className="mt-5 flex items-center justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-lg px-5 py-2 text-sm font-semibold text-[#111111] bg-[#9be38f] hover:bg-[#8ad37f] transition-colors"
            >
              Okay
            </button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistSourceDialog;
